/**
 * AssociationAddedItem - Renders one or more association additions.
 *
 * Display patterns:
 * - Many cardinality: "**Actor** added **N** to **Contacts**" with sub-items
 * - One cardinality: "**Actor** set **Contact**" with sub-item showing target
 *
 * Uses sub-items pattern similar to PropertyUpdatedEvent.
 */

import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { EntityBadge } from '@/lib/crm/components/badges/EntityBadge';
import { useEntityTypeLabel } from '@/lib/crm/hooks/useEntityTypeLabel';
import { useOpenSlideoutOptional } from '@/lib/crm/hooks/useOpenSlideout';
import { getEntityTypeSentenceLabel } from '@/lib/crm/utils/entityTypeUtils';
import { getAssociationTargetIconComponent } from '@/lib/crm/utils/icons/EntityTypeIcon';

import { FileAssociationCard } from '../components/FileAssociationCard';
import {
  ActorName,
  bold,
  InlineText,
  TimelineEvent,
} from '../components/shared';
import { SubjectReference } from '../components/SubjectReference';
import { useViewStateLookups } from '../context/TimelineLookupsContext';
import { isAssociationTypeVisible } from '../timelineItemVisibility';
import type { AssociationPayload, HydratedEntity } from '../types';
import { EVENT_TYPES } from '../types';
import type { SubItemContent } from '../types/renderer';
import { useEventFormatters } from '../useEventFormatters';
import type { ItemComponentProps } from './types';

function TargetBadge({
  entityId,
  entityType,
  displayName,
  onEntityClick,
  showOnClick,
}: {
  entityId: string;
  entityType: string;
  displayName: string;
  onEntityClick: (id: string) => void;
  showOnClick: boolean;
}) {
  const handleClick = useCallback(
    () => onEntityClick(entityId),
    [onEntityClick, entityId]
  );
  return (
    <EntityBadge
      state={{
        kind: 'data',
        data: { id: entityId, entityType, displayName },
      }}
      variant='xs'
      className='inline-flex align-middle'
      onClick={showOnClick ? handleClick : undefined}
    />
  );
}

function getFilePropsFromEntity(obj: HydratedEntity): {
  filename: string;
  fileSize: string | number | null;
  uploadedFileId: string | null;
} {
  const rawFilename = obj.properties.ahoy_filename?.value;
  const filename =
    rawFilename != null
      ? String(rawFilename)
      : (obj.ahoy_display_name ?? 'File');
  const rawFileSize = obj.properties.ahoy_file_size?.value;
  const fileSize =
    rawFileSize != null ? (rawFileSize as string | number) : null;
  const rawUploadedFileId = obj.properties.ahoy_uploaded_file_id?.value;
  const uploadedFileId =
    rawUploadedFileId != null ? String(rawUploadedFileId) : null;
  return { filename, fileSize, uploadedFileId };
}

export function AssociationAddedItem({
  item,
  entityType,
  isContext,
}: ItemComponentProps) {
  const { orgId } = useParams<{ orgId: string }>();
  const { getActorInfo, getObjectLabel } = useEventFormatters();
  const { lookups } = useViewStateLookups();
  const { getEntityTypeLabel } = useEntityTypeLabel(
    orgId ? Number(orgId) : undefined
  );
  const openSlideout = useOpenSlideoutOptional();

  const handleEntityClick = useCallback(
    (entityId: string) => {
      openSlideout?.(entityId);
    },
    [openSlideout]
  );

  const firstEvent = item.events[0];
  const actor = getActorInfo(firstEvent);
  const firstPayload = firstEvent.payload as AssociationPayload | undefined;

  // Get association type from lookups
  const assocTypeName = firstPayload?.association_type_name ?? '';
  const associationType = lookups.association_types[assocTypeName];

  // Check if this is an associated event (from a different entity)
  const isAssociatedEvent = 'association_type_name' in firstEvent;

  // For associated events, get the full object from lookups for badge display
  const associatedObject = isAssociatedEvent
    ? lookups.entities[firstEvent.entity_id]
    : undefined;

  const handleAssociatedObjectClick = useCallback(() => {
    if (associatedObject) {
      handleEntityClick(associatedObject.entity_id);
    }
  }, [handleEntityClick, associatedObject]);

  // Hide if association type not in lookups or is internal (fail-closed)
  if (!isAssociationTypeVisible(associationType)) {
    return null;
  }

  // For associated events, get the object label from lookups
  // For primary events, use "this {entityType}"
  const entityTypeDisplayLabel = getEntityTypeSentenceLabel(
    entityType,
    getEntityTypeLabel(entityType)
  );
  // Context node → the event's entity is not the record being viewed; use its
  // display name (badged below) instead of "this contact".
  const contextEntity = isContext
    ? lookups.entities[firstEvent.entity_id]
    : undefined;
  const objectDisplayText = isAssociatedEvent
    ? getObjectLabel(firstEvent.entity_id)
    : (contextEntity?.ahoy_display_name ?? `this ${entityTypeDisplayLabel}`);

  // Render associated object as badge if available, otherwise use text
  const isNoteObject = associatedObject?.entity_type_name === 'ahoy_note';
  const isFileObject = associatedObject?.entity_type_name === 'ahoy_file';
  let objectDisplay: ReactNode;
  if (isAssociatedEvent && associatedObject && isFileObject) {
    const { filename, fileSize, uploadedFileId } =
      getFilePropsFromEntity(associatedObject);
    objectDisplay = (
      <FileAssociationCard
        entityId={associatedObject.entity_id}
        filename={filename}
        fileSize={fileSize}
        uploadedFileId={uploadedFileId}
        orgId={orgId}
        onOpen={openSlideout ? handleEntityClick : undefined}
      />
    );
  } else if (isAssociatedEvent && associatedObject && !isNoteObject) {
    objectDisplay = (
      <EntityBadge
        state={{
          kind: 'data',
          data: {
            id: associatedObject.entity_id,
            entityType: associatedObject.entity_type_name,
            displayName: associatedObject.ahoy_display_name ?? 'Unknown',
          },
        }}
        variant='xs'
        className='inline-flex align-middle'
        onClick={openSlideout ? handleAssociatedObjectClick : undefined}
      />
    );
  } else if (isAssociatedEvent) {
    objectDisplay = bold(objectDisplayText);
  } else {
    // Primary node → "this contact"; context node → badge the subject entity.
    objectDisplay = (
      <SubjectReference
        isContext={isContext}
        entityId={firstEvent.entity_id}
        fallback={objectDisplayText}
        onEntityClick={openSlideout ? handleEntityClick : undefined}
      />
    );
  }

  const relationshipLabel = associationType?.label || 'association';
  const isManyCardinality =
    associationType?.cardinality === 'one_to_many' ||
    associationType?.cardinality === 'many_to_many';

  // Filter to only actual association events (cascade property_updated events
  // may be absorbed into this item by collapseCascadeEvents)
  const associationEvents = item.events.filter(
    e => e.event_type_name === EVENT_TYPES.ASSOCIATION_ADDED
  );

  // Collect all target objects and labels
  const targets: { object: HydratedEntity | undefined; label: string }[] =
    associationEvents.map(event => {
      const payload = event.payload as AssociationPayload | undefined;
      const targetObject = payload?.target_entity_id
        ? lookups.entities[payload.target_entity_id]
        : undefined;
      const targetLabel =
        payload?.target_label ||
        (payload?.target_entity_id
          ? getObjectLabel(payload.target_entity_id)
          : null) ||
        'record';
      return { object: targetObject, label: targetLabel };
    });

  // Get icon based on target entity type from association schema
  const IconComponent = getAssociationTargetIconComponent(
    associationType ?? {}
  );

  // Build sub-items for each target
  const subItems: SubItemContent[] = targets.map(target => {
    const isNoteTargetObject = target.object?.entity_type_name === 'ahoy_note';
    const isFileTargetObject = target.object?.entity_type_name === 'ahoy_file';
    const targetEntityId = target.object?.entity_id;

    let targetDisplay: ReactNode;
    if (target.object && isFileTargetObject) {
      const { filename, fileSize, uploadedFileId } = getFilePropsFromEntity(
        target.object
      );
      targetDisplay = (
        <FileAssociationCard
          entityId={target.object.entity_id}
          filename={filename}
          fileSize={fileSize}
          uploadedFileId={uploadedFileId}
          orgId={orgId}
          onOpen={openSlideout ? handleEntityClick : undefined}
        />
      );
    } else if (target.object && !isNoteTargetObject) {
      targetDisplay = (
        <TargetBadge
          entityId={target.object.entity_id}
          entityType={target.object.entity_type_name}
          displayName={target.object.ahoy_display_name ?? 'Unknown'}
          onEntityClick={handleEntityClick}
          showOnClick={!!openSlideout && !!targetEntityId}
        />
      );
    } else {
      targetDisplay = <span className='font-medium'>{target.label}</span>;
    }

    return {
      icon: <IconComponent className='w-4 h-4 text-primary' />,
      text: targetDisplay,
      ariaLabel: target.label,
    };
  });

  // Build header message
  const ariaTargetLabels = targets.map(t => t.label).join(', ');

  let headerContent: React.ReactNode;
  let ariaLabel: string;

  if (isManyCardinality) {
    // Many: "Actor added Contacts to this deal"
    ariaLabel = `${actor.name} added ${ariaTargetLabels} to ${objectDisplayText}`;
    headerContent = (
      <>
        <ActorName actor={actor} /> added {bold(relationshipLabel)} to{' '}
        {objectDisplay}
      </>
    );
  } else {
    // One: "Actor set Contact on this deal"
    ariaLabel = `${actor.name} set ${relationshipLabel} to ${ariaTargetLabels} on ${objectDisplayText}`;
    headerContent = (
      <>
        <ActorName actor={actor} /> set {bold(relationshipLabel)} on{' '}
        {objectDisplay}
      </>
    );
  }

  return (
    <TimelineEvent ariaLabel={ariaLabel}>
      <TimelineEvent.Icon event_type_name={EVENT_TYPES.ASSOCIATION_ADDED} />
      <TimelineEvent.Content
        header={<InlineText>{headerContent}</InlineText>}
        timestamp={firstEvent.occurred_at}
        subItems={subItems}
      />
    </TimelineEvent>
  );
}
